/**
 * <delivery-estimate>: fills a three-step timeline (ordered → shipped → delivered)
 * with concrete dates computed from merchant-set business-day ranges.
 * Business days are Mon–Fri excluding the merchant's closure dates.
 * The Liquid fallback sentence stays visible when inputs are invalid or JS is off.
 */
if (!customElements.get('delivery-estimate')) {
  customElements.define(
    'delivery-estimate',
    class DeliveryEstimate extends HTMLElement {
      connectedCallback() {
        const num = (key) => parseInt(this.dataset[key], 10);
        const productionMin = num('productionMin');
        const productionMax = num('productionMax');
        const shippingMin = num('shippingMin');
        const shippingMax = num('shippingMax');
        if ([productionMin, productionMax, shippingMin, shippingMax].some(Number.isNaN)) return;

        this.closures = new Set(
          (this.dataset.closures || '')
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean)
        );

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const shipFrom = this.addBusinessDays(today, productionMin);
        const shipTo = this.addBusinessDays(today, productionMax);
        const deliverFrom = this.addBusinessDays(shipFrom, shippingMin);
        const deliverTo = this.addBusinessDays(shipTo, shippingMax);

        this.fill('ordered', today, today);
        this.fill('shipped', shipFrom, shipTo);
        this.fill('delivered', deliverFrom, deliverTo);

        const fallback = this.querySelector('[data-delivery-fallback]');
        const timeline = this.querySelector('[data-delivery-timeline]');
        if (fallback) fallback.hidden = true;
        if (timeline) timeline.hidden = false;

        // Feature 011 compact layout: one line with the arrival window, breakdown in <details>
        const windowTarget = this.querySelector('[data-delivery-window]');
        if (windowTarget) {
          const range = document.createElement('span');
          this.fillRange(range, deliverFrom, deliverTo);
          windowTarget.replaceChildren(...range.childNodes);
          this.querySelector('[data-delivery-line]')?.removeAttribute('hidden');
          this.querySelector('[data-delivery-details]')?.removeAttribute('hidden');
        }
      }

      isoLocal(date) {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${date.getFullYear()}-${month}-${day}`;
      }

      isBusinessDay(date) {
        const weekday = date.getDay();
        return weekday !== 0 && weekday !== 6 && !this.closures.has(this.isoLocal(date));
      }

      addBusinessDays(start, count) {
        const date = new Date(start);
        let added = 0;
        while (added < count) {
          date.setDate(date.getDate() + 1);
          if (this.isBusinessDay(date)) added += 1;
        }
        return date;
      }

      format(date) {
        return date.toLocaleDateString(document.documentElement.lang || 'en-US', { month: 'short', day: 'numeric' });
      }

      fill(step, from, to) {
        const target = this.querySelector(`[data-step="${step}"] [data-dates]`);
        if (!target) return;
        this.fillRange(target, from, to);
      }

      fillRange(target, from, to) {
        const fromTime = document.createElement('time');
        fromTime.dateTime = this.isoLocal(from);
        fromTime.textContent = this.format(from);
        target.replaceChildren(fromTime);
        if (this.isoLocal(from) !== this.isoLocal(to)) {
          const toTime = document.createElement('time');
          toTime.dateTime = this.isoLocal(to);
          toTime.textContent = this.format(to);
          target.append(' – ', toTime);
        }
      }
    }
  );
}
